import { CRITERION_VALUE } from "@/constants/criterion-values"

import type { Annotation } from "@/types/api/Annotation"
import type { MetricMetadata } from "@/types/metrics/metadata"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"
import { formatNumber } from "@/utils/formatting/format-number"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import {
  getCompositeResultData,
  isAllCriteriaMetPass,
  type CompositeEvaluationResultJson,
  type CompositeResultData,
} from "./composite-evaluation-types"

interface VersionedMetricOutput {
  result?: Record<string, unknown> | null
  result_json?: Record<string, unknown> | null
}

interface ComparableMetricValue {
  float_value?: number | null
  string_value?: string | null
  set_value?: string[] | null
}

interface HumanReviewValue {
  output_type: string
  float_value: number | null
  string_value: string | null
  set_value: string[] | null
  json_value: Record<string, unknown> | null
}

export function getCompositeReportingMethod(
  metricMetadata?: MetricMetadata,
  resultData?: CompositeResultData | CompositeEvaluationResultJson,
): string {
  return (
    metricMetadata?.reporting_method ||
    (resultData && "reporting_method" in resultData
      ? resultData.reporting_method
      : undefined) ||
    "percentage_of_criteria_met"
  )
}

export function isCompositeMetricOutputCurrentVersion(
  metricMetadata: MetricMetadata | undefined,
  metricOutput: VersionedMetricOutput,
): boolean {
  const resultJson = getCompositeResultData(metricOutput)
  if (!resultJson) {
    return true
  }
  const currentCriteria = metricMetadata?.criteria
  const executionCriteria = resultJson.individual_results
  if (!Array.isArray(currentCriteria) || !Array.isArray(executionCriteria)) {
    return true
  }
  if (currentCriteria.length !== executionCriteria.length) {
    return false
  }
  const executionTexts = executionCriteria.map((criterion) =>
    stringifyUnknownValue(criterion.criterion ?? ""),
  )
  return !currentCriteria.some(
    (criterion, index) => criterion !== executionTexts[index],
  )
}

export function getCompositeVersionDiff(
  metricMetadata: MetricMetadata | undefined,
  metricOutput: VersionedMetricOutput,
): Array<{ field: string; current: string; previous: string }> {
  const resultJson = getCompositeResultData(metricOutput)
  if (!resultJson) {
    return []
  }
  const currentCriteria = metricMetadata?.criteria
  const executionCriteria = resultJson.individual_results
  if (!Array.isArray(currentCriteria) || !Array.isArray(executionCriteria)) {
    return []
  }
  if (currentCriteria.length !== executionCriteria.length) {
    return [
      {
        field: "Criteria Count",
        current: String(currentCriteria.length),
        previous: String(executionCriteria.length),
      },
    ]
  }
  const executionTexts = executionCriteria.map((criterion) =>
    stringifyUnknownValue(criterion.criterion ?? ""),
  )
  const changedCriteria = currentCriteria.filter(
    (criterion, index) => criterion !== executionTexts[index],
  )
  return changedCriteria.length > 0
    ? [
        {
          field: "Criteria Text",
          current: `${changedCriteria.length} criterion/criteria changed`,
          previous: "See individual criteria",
        },
      ]
    : []
}

function extractCriteriaResults(
  resultJson?: Record<string, unknown> | null,
): string[] | null {
  const individualResults = resultJson?.individual_results
  if (!Array.isArray(individualResults) || individualResults.length === 0) {
    return null
  }
  return individualResults.map((result: { result?: string }) => {
    return result.result ?? CRITERION_VALUE.UNKNOWN
  })
}

export function compareCompositeValues(
  firstValue: ComparableMetricValue,
  secondValue: ComparableMetricValue,
): boolean {
  const firstSet = firstValue.set_value
  const secondSet = secondValue.set_value
  if (firstSet && secondSet && firstSet.length === secondSet.length) {
    return firstSet.every((value, index) => value === secondSet[index])
  }
  if (firstValue.float_value != null && secondValue.float_value != null) {
    return firstValue.float_value === secondValue.float_value
  }
  return false
}

export function compositeAnnotationMatchesMetricOutput(
  annotation: Annotation,
  metricOutput: {
    output_type: string
    float_value: number | null
    string_value: string | null
    set_value: string[] | null
    result_json?: Record<string, unknown> | null
  },
): boolean {
  const humanCriteria = annotation.ground_truth_set_value
  if (humanCriteria && humanCriteria.length > 0) {
    const compositeData = getCompositeResultData(metricOutput)
    const aiCriteria =
      metricOutput.set_value ??
      extractCriteriaResults(
        compositeData as Record<string, unknown> | undefined,
      )
    if (aiCriteria && aiCriteria.length === humanCriteria.length) {
      return humanCriteria.every((value, index) => value === aiCriteria[index])
    }
  }
  return compareCompositeValues(
    {
      float_value: annotation.ground_truth_float_value,
      string_value: annotation.ground_truth_string_value,
      set_value: annotation.ground_truth_set_value,
    },
    metricOutput,
  )
}

function getFloatFallbackAggregationValue(
  metricOutput: { float_value?: number | null },
  reportingMethod: string,
  metricMetadata?: MetricMetadata,
): number | null {
  const floatValue = metricOutput.float_value
  if (floatValue == null || !Number.isFinite(floatValue)) {
    return null
  }
  if (reportingMethod === "all_criteria_met") {
    return floatValue >= 1 ? 100 : 0
  }
  if (reportingMethod === "count_of_criteria_met") {
    const criteria = metricMetadata?.criteria
    return Array.isArray(criteria) ? floatValue * criteria.length : null
  }
  return floatValue * 100
}

export function getCompositeNumericValues(
  metricOutputs: RunMetricOutput[],
  metricMetadata?: MetricMetadata,
): number[] {
  const values: number[] = []
  for (const metricOutput of metricOutputs) {
    const resultJson = getCompositeResultData(metricOutput)
    const reportingMethod = getCompositeReportingMethod(
      metricMetadata,
      resultJson,
    )
    if (!resultJson?.summary) {
      const fallback = getFloatFallbackAggregationValue(
        metricOutput,
        reportingMethod,
        metricMetadata,
      )
      if (fallback != null) {
        values.push(fallback)
      }
      continue
    }

    const { criteria_met_count, criteria_evaluated_count } = resultJson.summary
    if (reportingMethod === "all_criteria_met") {
      values.push(isAllCriteriaMetPass(resultJson.summary) ? 100 : 0)
    } else if (
      reportingMethod === "percentage_of_criteria_met" &&
      criteria_evaluated_count > 0
    ) {
      const percentage = (criteria_met_count / criteria_evaluated_count) * 100
      if (Number.isFinite(percentage)) {
        values.push(percentage)
      }
    } else if (
      reportingMethod === "count_of_criteria_met" &&
      Number.isFinite(criteria_met_count)
    ) {
      values.push(criteria_met_count)
    }
  }
  return values
}

function getFloatFallbackHeaderValue(
  metricOutputs: RunMetricOutput[],
  reportingMethod: string,
  metricMetadata?: MetricMetadata,
): string | null {
  const values = metricOutputs
    .map((metricOutput) =>
      getFloatFallbackAggregationValue(
        metricOutput,
        reportingMethod,
        metricMetadata,
      ),
    )
    .filter((value): value is number => value != null)
  if (values.length === 0) {
    return null
  }
  if (reportingMethod === "all_criteria_met") {
    const passed = values.filter((value) => value >= 100).length
    return `${passed}/${metricOutputs.length} passed`
  }
  const average = values.reduce((sum, value) => sum + value, 0) / values.length
  return reportingMethod === "count_of_criteria_met"
    ? formatNumber(average)
    : `${formatNumber(average)}%`
}

export function getCompositeHeaderValue(
  metricOutputs: RunMetricOutput[],
  metricMetadata?: MetricMetadata,
): string | null {
  const reportingMethod = getCompositeReportingMethod(
    metricMetadata,
    getCompositeResultData(metricOutputs[0]),
  )
  if (
    !metricOutputs.some(
      (metricOutput) => getCompositeResultData(metricOutput)?.summary,
    )
  ) {
    return getFloatFallbackHeaderValue(
      metricOutputs,
      reportingMethod,
      metricMetadata,
    )
  }
  if (reportingMethod === "all_criteria_met") {
    const passed = metricOutputs.filter((metricOutput) => {
      const resultJson = getCompositeResultData(metricOutput)
      return resultJson?.summary
        ? isAllCriteriaMetPass(resultJson.summary)
        : false
    }).length
    return `${passed}/${metricOutputs.length} passed`
  }

  const totalMet = metricOutputs.reduce((sum, metricOutput) => {
    return (
      sum +
      (getCompositeResultData(metricOutput)?.summary?.criteria_met_count || 0)
    )
  }, 0)
  const totalEvaluated = metricOutputs.reduce((sum, metricOutput) => {
    return (
      sum +
      (getCompositeResultData(metricOutput)?.summary
        ?.criteria_evaluated_count || 0)
    )
  }, 0)
  if (totalEvaluated === 0) {
    return null
  }
  if (reportingMethod === "percentage_of_criteria_met") {
    return `${formatNumber((totalMet / totalEvaluated) * 100)}%`
  }
  if (reportingMethod === "count_of_criteria_met") {
    return formatNumber(totalMet / metricOutputs.length)
  }
  return `${totalMet}/${totalEvaluated}`
}

export function getCompositeDisplayValue(
  metricOutput: MetricOutputDisplaySource,
  metricMetadata?: MetricMetadata,
): string | null {
  if (metricOutput.float_value === null) {
    return null
  }
  const resultJson = getCompositeResultData(metricOutput)
  const reportingMethod = getCompositeReportingMethod(
    metricMetadata,
    resultJson,
  )
  if (!resultJson?.summary) {
    const fallback = getFloatFallbackAggregationValue(
      metricOutput,
      reportingMethod,
      metricMetadata,
    )
    if (fallback == null) {
      return null
    }
    if (reportingMethod === "all_criteria_met") {
      return fallback >= 100 ? "Pass" : "Fail"
    }
    return reportingMethod === "count_of_criteria_met"
      ? formatNumber(fallback)
      : `${formatNumber(fallback)}%`
  }
  const { criteria_met_count, criteria_evaluated_count } = resultJson.summary
  if (reportingMethod === "all_criteria_met") {
    return isAllCriteriaMetPass(resultJson.summary) ? "Pass" : "Fail"
  }
  if (reportingMethod === "percentage_of_criteria_met") {
    const percentage =
      criteria_evaluated_count > 0
        ? (criteria_met_count / criteria_evaluated_count) * 100
        : 0
    return `${formatNumber(percentage)}%`
  }
  if (reportingMethod === "count_of_criteria_met") {
    return String(criteria_met_count)
  }
  return `${criteria_met_count}/${criteria_evaluated_count}`
}

export function getCompositeHumanReviewDisplayValue(
  review: HumanReviewValue,
  metricMetadata?: MetricMetadata,
): string | null {
  const jsonValue = review.json_value as unknown as
    | CompositeEvaluationResultJson
    | undefined
  const reportingMethod = getCompositeReportingMethod(metricMetadata, jsonValue)
  if (jsonValue?.summary) {
    const { criteria_met_count, criteria_evaluated_count } = jsonValue.summary
    if (reportingMethod === "all_criteria_met") {
      return isAllCriteriaMetPass(jsonValue.summary) ? "Pass" : "Fail"
    }
    if (reportingMethod === "percentage_of_criteria_met") {
      const percentage =
        criteria_evaluated_count > 0
          ? (criteria_met_count / criteria_evaluated_count) * 100
          : 0
      return `${formatNumber(percentage)}%`
    }
    if (reportingMethod === "count_of_criteria_met") {
      return String(criteria_met_count)
    }
    return `${criteria_met_count}/${criteria_evaluated_count}`
  }

  if (review.set_value && review.set_value.length > 0) {
    const metCount = review.set_value.filter(
      (value) => value === CRITERION_VALUE.MET,
    ).length
    const evaluatedCount = review.set_value.filter(
      (value) =>
        value === CRITERION_VALUE.MET || value === CRITERION_VALUE.NOT_MET,
    ).length
    if (reportingMethod === "all_criteria_met") {
      return metCount === review.set_value.length ? "Pass" : "Fail"
    }
    if (reportingMethod === "percentage_of_criteria_met") {
      const percentage =
        evaluatedCount > 0 ? (metCount / evaluatedCount) * 100 : 0
      return `${formatNumber(percentage)}%`
    }
    if (reportingMethod === "count_of_criteria_met") {
      return String(metCount)
    }
    return `${metCount}/${review.set_value.length}`
  }
  if (
    typeof review.float_value !== "number" ||
    !Number.isFinite(review.float_value)
  ) {
    return null
  }
  return `${formatNumber(review.float_value * 100)}%`
}

/**
 * A judge's overall result for one composite metric, as it reads in the totals
 * row: "Pass"/"Fail" for all_criteria_met, a bare count for
 * count_of_criteria_met, a percentage otherwise. `null` when the judge has not
 * ruled on every criterion, so a partial vote never shows as a low score.
 *
 * Both judges score the same way — the AI from its per-criterion verdicts, the
 * reviewer from their votes — so the two totals are comparable.
 */
export function getCompositeJudgeScore(
  values: readonly (string | null | undefined)[] | null | undefined,
  reportingMethod: string,
  criteriaCount: number,
): { label: string; passed: boolean | null } | null {
  if (!values || criteriaCount === 0 || values.length !== criteriaCount) {
    return null
  }
  const isRuled = (v: string | null | undefined) =>
    v === CRITERION_VALUE.MET ||
    v === CRITERION_VALUE.NOT_MET ||
    v === CRITERION_VALUE.UNKNOWN
  if (!values.every(isRuled)) {
    return null
  }
  const metCount = values.filter((v) => v === CRITERION_VALUE.MET).length
  if (reportingMethod === "all_criteria_met") {
    const passed = metCount === criteriaCount
    return { label: passed ? "Pass" : "Fail", passed }
  }
  if (reportingMethod === "count_of_criteria_met") {
    return { label: String(metCount), passed: null }
  }
  const evaluatedCount = values.filter(
    (v) => v === CRITERION_VALUE.MET || v === CRITERION_VALUE.NOT_MET,
  ).length
  const pct = evaluatedCount > 0 ? (metCount / evaluatedCount) * 100 : 0
  return { label: `${formatNumber(pct)}%`, passed: null }
}
